import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import { Modal, Divider } from 'antd';
import SelectEdit from '../../components/SelectEdit';

import selector from './gatesPressingEditor.selector';
import { setSelected } from '../Construction/construction.reducer';

import { availableType } from '../../types/available.types';
import { infoShape } from '../../types/info.types';

import {
  ModalContainer,
  StyledImg,
  ImgWrapper
} from './gatesPressingEditor.styles';

const ALL_GATE_TYPES_ORDER = [
  'swing_gate',
  'double_leaf_gate',
  'sectional_gate',
  'roller_gate'
];

const GATE_TYPE_I18N_KEYS = {
  swing_gate: 'gate_type.swing_gate',
  double_leaf_gate: 'gate_type.double_leaf_gate',
  sectional_gate: 'gate_type.sectional_gate',
  roller_gate: 'gate_type.roller_gate'
};

const GATE_TYPE_FALLBACK_LABELS = {
  swing_gate: 'Brama uchylna',
  double_leaf_gate: 'Brama dwuskrzydłowa',
  sectional_gate: 'Brama segmentowa',
  roller_gate: 'Brama rolowana'
};

const GATE_TYPES_WITHOUT_PRESSING = [
  'sectional_gate',
  'roller_gate'
];

class GatesPressingEditor extends React.Component {
  static propTypes = {
    loaded: PropTypes.bool.isRequired,
    setSelected: PropTypes.func.isRequired,
    available: PropTypes.arrayOf(availableType),
    info: infoShape,

    translations: PropTypes.object,
    gateTypesPresent: PropTypes.arrayOf(PropTypes.string),

    selectedByType: PropTypes.shape({
      swing_gate: PropTypes.number,
      double_leaf_gate: PropTypes.number,
      sectional_gate: PropTypes.number,
      roller_gate: PropTypes.number
    }),

    selectedColorByType: PropTypes.shape({
      swing_gate: PropTypes.number,
      double_leaf_gate: PropTypes.number,
      sectional_gate: PropTypes.number,
      roller_gate: PropTypes.number
    }),

    colorsAvailableColors: PropTypes.object
  };

  static defaultProps = {
    available: [],
    translations: {},
    gateTypesPresent: [],
    selectedByType: {
      swing_gate: null,
      double_leaf_gate: null,
      sectional_gate: null,
      roller_gate: null
    },
    selectedColorByType: {
      swing_gate: null,
      double_leaf_gate: null,
      sectional_gate: null,
      roller_gate: null
    },
    colorsAvailableColors: {},
    info: null
  };

  state = {
    modalVisible: false,
    activeGateType: 'swing_gate'
  };

  componentDidUpdate(prevProps) {
    if (prevProps.gateTypesPresent !== this.props.gateTypesPresent) {
      const visible = this.getEditableGateTypes();

      if (visible.length === 0) {
        if (this.state.modalVisible) {
          this.setState({ modalVisible: false });
        }
        return;
      }

      if (!visible.includes(this.state.activeGateType)) {
        this.setState({
          activeGateType: visible[0],
          modalVisible: false
        });
      }
    }

    if (
      this.state.modalVisible &&
      GATE_TYPES_WITHOUT_PRESSING.includes(this.state.activeGateType)
    ) {
      this.closeModal();
    }
  }

  getVisibleGateTypes = () => {
    const present = new Set(this.props.gateTypesPresent || []);
    return ALL_GATE_TYPES_ORDER.filter((t) => present.has(t));
  };

  getEditableGateTypes = () => {
    return this.getVisibleGateTypes().filter(
      (gateType) => !GATE_TYPES_WITHOUT_PRESSING.includes(gateType)
    );
  };

  t = (key, fallback = '') => {
    if (!key) return fallback;
    const { translations } = this.props;
    const value = String(key)
      .split('.')
      .reduce((acc, part) => (acc && acc[part] !== undefined ? acc[part] : undefined), translations);

    if (value === undefined || value === null || value === '') return fallback;
    return value;
  };

  getGateTypeLabel = (gateType) => {
    const key = GATE_TYPE_I18N_KEYS[gateType];
    return this.t(key, GATE_TYPE_FALLBACK_LABELS[gateType] || gateType);
  };

  openModal = (gateType) => {
    if (GATE_TYPES_WITHOUT_PRESSING.includes(gateType)) return;

    this.setState({ modalVisible: true, activeGateType: gateType });
  };

  closeModal = () => {
    this.setState({ modalVisible: false });
  };

  getAllowedPressingIdsForGateType = (gateType) => {
    const colorId = this.props.selectedColorByType?.[gateType] ?? null;
    if (!colorId) return null;

    const allowed = this.props.colorsAvailableColors?.[colorId];
    if (!Array.isArray(allowed)) return null;

    return allowed;
  };

  isPressingAvailableForGateType = (gateType, pressingId) => {
    if (GATE_TYPES_WITHOUT_PRESSING.includes(gateType)) return false;

    const allowedPressingIds = this.getAllowedPressingIdsForGateType(gateType);
    if (!allowedPressingIds) return true;

    return allowedPressingIds.includes(pressingId);
  };

  handleInsideChange = (gateType, value) => {
    if (GATE_TYPES_WITHOUT_PRESSING.includes(gateType)) return;
    if (!this.isPressingAvailableForGateType(gateType, value)) return;

    const current = this.props.selectedByType?.[gateType] ?? null;

    if (value !== current) {
      this.props.setSelected(`gates_pressing_${gateType}`, value);
      this.closeModal();
    }
  };

  renderSelectForType = (gateType) => {
    if (GATE_TYPES_WITHOUT_PRESSING.includes(gateType)) {
      return null;
    }

    const selected = this.props.selectedByType?.[gateType] ?? null;

    return (
      <SelectEdit
        key={`pressing_${gateType}`}
        selected={selected}
        loaded={this.props.loaded}
        onEditClick={() => this.openModal(gateType)}
        title={this.getGateTypeLabel(gateType)}
        options={(this.props.available || []).map((el) => ({
          id: el.id,
          value: `${el.attributes.value}`
        }))}
      />
    );
  };

  render() {
    if (!this.props.loaded) return <SelectEdit loading />;

    const editableGateTypes = this.getEditableGateTypes();

    if (editableGateTypes.length === 0) {
      return null;
    }

    const { info } = this.props;
    const activeGateType = editableGateTypes.includes(this.state.activeGateType)
      ? this.state.activeGateType
      : editableGateTypes[0];

    const activeSelected = this.props.selectedByType?.[activeGateType] ?? null;

    return (
      <React.Fragment>
        {editableGateTypes.map(this.renderSelectForType)}

        <Modal
          title={`${info?.name || 'Przetłoczenie'} - ${this.getGateTypeLabel(activeGateType)}`}
          open={this.state.modalVisible}
          footer={null}
          onCancel={this.closeModal}
        >
          <ModalContainer>
            {(this.props.available || []).map((el) => {
              const isAvailable = this.isPressingAvailableForGateType(activeGateType, el.id);

              return (
                <div key={`inside__${activeGateType}__${el.id}`}>
                  <ImgWrapper
                    $selected={el.id === activeSelected}
                    onClick={isAvailable ? () => this.handleInsideChange(activeGateType, el.id) : () => {}}
                    style={{
                      opacity: isAvailable ? 1 : 0.45,
                      cursor: isAvailable ? 'pointer' : 'not-allowed'
                    }}
                  >
                    <StyledImg
                      src={el.attributes.img}
                      style={{
                        filter: isAvailable ? 'none' : 'grayscale(100%)'
                      }}
                    />
                  </ImgWrapper>
                  <p style={{ opacity: isAvailable ? 1 : 0.6 }}>
                    {el.attributes.value}
                  </p>
                </div>
              );
            })}
          </ModalContainer>

          <Divider>{info?.info_title}</Divider>
          {info?.info_content}
        </Modal>
      </React.Fragment>
    );
  }
}

const mapDispatchToProps = {
  setSelected
};

export default connect(selector, mapDispatchToProps)(GatesPressingEditor);
